import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type HandOnOptions, type LeaderOption, type ProjectRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { toDateTimeLocal } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';
import { AttachmentList, AttachmentsField, useUploads } from '../components/Attachments';
import { NameField, hasDescription, nameFits } from '../components/NameField';
import {
  WorkflowBuilder,
  everyoneOnLevels,
  levelsReady,
  startLevels,
  workflowPayload,
  type LevelDraft,
} from '../components/WorkflowBuilder';
import { withCurrent } from './TicketForm';
import { useLeaderTaskRole } from './LeaderTasks';
import { refusalText } from '../lib/reach';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/** A leader and one department they lead — what a Leaders Task is sent to. */
type LeaderChoice = { key: string; leader: UserRef; department: { id: string; name: string; nameAr: string | null } };

function leaderChoices(leaders: LeaderOption[] | undefined): LeaderChoice[] {
  return (leaders ?? []).flatMap((leader) =>
    leader.departments.map((department) => ({ key: `${leader.id}|${department.id}`, leader, department })),
  );
}

/** "Leader B — Development", in the reader's language. */
function useLeaderLabel() {
  const { t, localized } = useI18n();
  return (choice: { leader: UserRef; department: { name: string; nameAr?: string | null } }) =>
    t('leaderTasks.leaderOf', { name: localized(choice.leader), department: localized(choice.department) });
}

/** A warning above a form's fields. */
function Notice({ children, tone = 'warning' }: { children: ReactNode; tone?: 'warning' | 'info' }) {
  return (
    <p
      className={clsx(
        'rounded-md border px-3 py-2 text-body-sm',
        tone === 'warning' ? 'border-warning/35 bg-warning/8 text-warning-fg dark:text-warning-dark' : 'border-info/30 bg-info/8 text-info-fg dark:text-info-dark',
      )}
    >
      {children}
    </p>
  );
}

/**
 * Route: `/leader-tasks/new` — a department leader (or an admin) asks a leader
 * of a department for something, with everything a task carries: its name,
 * words, project, priority, dates and files. The leader asked hears by bell
 * and email.
 */
export function LeaderTaskFormPage() {
  const { t, locale, localized } = useI18n();
  const { maySend } = useLeaderTaskRole();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();
  const leaderLabel = useLeaderLabel();
  const [form, setForm] = useState({
    title: '',
    description: '',
    to: '',
    projectId: '',
    priority: 'MEDIUM',
    startAt: '',
    dueAt: '',
  });
  const datesBackwards = !!form.startAt && !!form.dueAt && new Date(form.dueAt) <= new Date(form.startAt);

  const leaders = useQuery({
    queryKey: ['leader-tasks', 'leaders'],
    queryFn: () => api.get<LeaderOption[]>('/leader-tasks/leaders'),
    enabled: maySend,
  });
  const { data: projects } = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options'),
  });
  // A Leaders Task goes to another leader.
  const choices = leaderChoices(leaders.data).filter((c) => c.leader.id !== user?.id);
  const chosen = choices.find((c) => c.key === form.to) ?? null;

  const send = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/leader-tasks', {
        title: form.title,
        description: form.description,
        assigneeUserId: chosen!.leader.id,
        departmentId: chosen!.department.id,
        projectId: form.projectId || undefined,
        priority: form.priority,
        // datetime-local is the browser's own clock; the API wants an instant.
        startAt: form.startAt ? new Date(form.startAt).toISOString() : undefined,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['leader-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(t('leaderTasks.sentTo', { name: chosen ? localized(chosen.leader) : '' }));
      navigate(`/leader-tasks/${created.id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  return (
    <FormPage
      title={t('leaderTasks.new')}
      backLabel={t('nav.leaderTasks')}
      submitLabel={t('common.create')}
      submitting={send.isPending}
      loading={maySend && leaders.isLoading}
      error={leaders.error}
      onRetry={() => void leaders.refetch()}
      // Saving while a file is still uploading would leave it behind.
      disabled={
        !maySend ||
        !nameFits(form.title) ||
        !hasDescription(form.description) ||
        !chosen ||
        uploads.busy ||
        datesBackwards
      }
      onSubmit={() => send.mutate()}
    >
      <div className="space-y-4">
        {!maySend && <Notice>{t('leaderTasks.onlyLeaders')}</Notice>}
        {maySend && leaders.data && choices.length === 0 && <Notice tone="info">{t('leaderTasks.noLeaders')}</Notice>}
        <NameField
          label={t('common.name')}
          value={form.title}
          onChange={(title) => setForm({ ...form, title })}
          disabled={!maySend}
          dir="auto"
          autoFocus
        />
        <Field label={t('common.description')} required>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={!maySend}
            dir="auto"
          />
        </Field>
        <Field label={t('leaderTasks.leader')} required hint={t('leaderTasks.leaderHint')}>
          <Select value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} disabled={!maySend}>
            <option value="">—</option>
            {choices.map((choice) => (
              <option key={choice.key} value={choice.key}>{leaderLabel(choice)}</option>
            ))}
          </Select>
        </Field>
        <FormGrid>
          <Field label={t('common.project')} hint={t('tasks.generalHint')}>
            <Select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              disabled={!maySend}
            >
              <option value="">{t('common.general')}</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>{localized(p)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.priority')}>
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              disabled={!maySend}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('tasks.startDate')}>
            <Input
              type="datetime-local"
              value={form.startAt}
              onChange={(e) => setForm({ ...form, startAt: e.target.value })}
              disabled={!maySend}
            />
          </Field>
          <Field label={t('tasks.deadline')} error={datesBackwards ? t('tasks.dueAfterStart') : null}>
            <Input
              type="datetime-local"
              value={form.dueAt}
              min={form.startAt || undefined}
              onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
              disabled={!maySend}
            />
          </Field>
        </FormGrid>
        {maySend && <AttachmentsField uploads={uploads} />}
      </div>
    </FormPage>
  );
}

/** Two choices side by side, as cards: the one picked is ringed. */
function ChoiceCards<T extends string>({
  value,
  onChange,
  options,
  name,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string; hint: string; disabled?: boolean }>;
  name: string;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
      {options.map((option) => (
        <label
          key={option.value}
          className={clsx(
            'flex cursor-pointer gap-2.5 rounded-md border px-3 py-2.5 transition-colors duration-fast',
            value === option.value ? 'border-primary bg-primary/8' : 'hairline hover:bg-[var(--surface-sunken)]',
            option.disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <input
            type="radio"
            name={name}
            className="mt-1 accent-primary"
            checked={value === option.value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
          />
          <span className="min-w-0">
            <span className="block text-body-sm font-medium text-[var(--text-primary)]">{option.label}</span>
            <span className="block text-caption text-muted">{option.hint}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

type HandOnForm = {
  title: string;
  description: string;
  projectId: string;
  to: 'person' | 'workflow';
  assigneeUserId: string;
  priority: string;
  startAt: string;
  dueAt: string;
};

/**
 * Route: `/leader-tasks/:id/hand-on` — the leader asked hands the work on: to
 * someone in a department they lead, or to a workflow built here (2026-10-15)
 * of those same people, with a start and a deadline. The task it becomes starts
 * from the Leaders Task's words, project and priority, and carries its files.
 * When that work is finished, the Leaders Task comes back to its leader for
 * review.
 */
export function LeaderTaskHandOnPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();

  const existing = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const task = existing.data;
  const [form, setForm] = useState<HandOnForm | null>(null);
  if (task && !form) {
    setForm({
      title: task.title,
      description: task.description ?? '',
      projectId: task.projectId ?? '',
      to: 'person',
      assigneeUserId: '',
      priority: task.priority,
      startAt: toDateTimeLocal(task.startAt),
      dueAt: toDateTimeLocal(task.dueAt),
    });
  }
  const f: HandOnForm = form ?? {
    title: '',
    description: '',
    projectId: '',
    to: 'person',
    assigneeUserId: '',
    priority: 'MEDIUM',
    startAt: '',
    dueAt: '',
  };
  const set = (patch: Partial<HandOnForm>) => setForm({ ...f, ...patch });
  const [levels, setLevels] = useState<LevelDraft[]>(startLevels);

  // Whom the leader can hand it to, marked on or off the project chosen.
  const options = useQuery({
    queryKey: ['leader-tasks', id, 'hand-on', f.projectId],
    queryFn: () => api.get<HandOnOptions>(`/leader-tasks/${id}/hand-on`, { projectId: f.projectId || undefined }),
    enabled: !!task?.isLeaderTask,
  });
  const { data: projects } = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options'),
  });
  // The Leaders Task's own project stays on offer, whether or not the leader is on it.
  const projectChoices = withCurrent(projects ?? [], task?.project ?? null);
  const people = (options.data?.departments ?? []).flatMap((d) => d.people);
  const person = people.find((p) => p.id === f.assigneeUserId) ?? null;
  // Each once, whichever of the leader's departments they are in: the workflow's people (2026-10-15).
  const everyone = [...new Map(people.map((p) => [p.id, p])).values()];
  // §8.4 — on a project task, whoever on the workflow is off the project.
  const offProjectOnWorkflow = new Set(
    f.to === 'workflow' ? everyoneOnLevels(levels).filter((uid) => everyone.find((p) => p.id === uid)?.onProject === false) : [],
  );
  const leaderName = localized(options.data?.leader ?? task?.assignee);
  const overrides = can(P.TASKS_MANAGE_ALL);
  const offProject = (f.to === 'person' && !!person && person.onProject === false) || offProjectOnWorkflow.size > 0;
  const datesBackwards = !!f.startAt && !!f.dueAt && new Date(f.dueAt) <= new Date(f.startAt);
  const nobody = !!options.data && people.length === 0;

  const may = task?.can ?? {};
  const blocked: string | null = !task
    ? null
    : !task.isLeaderTask
      ? t('common.error')
      : may.handOn
        ? null
        : task.leaderWork?.state === 'underWay'
          ? t('leaderTasks.cannotHandOnUnderWay')
          : ['TO_DO', 'IN_PROGRESS', 'BLOCKED'].includes(task.status)
            ? t('leaderTasks.cannotHandOnNotYours')
            : t('leaderTasks.cannotHandOnStatus', { status: enumLabel(task.status, locale) });

  const handOn = useMutation({
    mutationFn: () =>
      api.post(`/leader-tasks/${id}/hand-on`, {
        title: f.title,
        description: f.description,
        projectId: f.projectId || null,
        ...(f.to === 'workflow' ? { workflow: workflowPayload(levels) } : { assigneeUserId: f.assigneeUserId }),
        priority: f.priority,
        startAt: new Date(f.startAt).toISOString(),
        dueAt: new Date(f.dueAt).toISOString(),
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      for (const queryKey of [['task', id], ['leader-tasks'], ['tasks'], ['kanban'], ['dashboard']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      toast.success(
        f.to === 'workflow'
          ? t('leaderTasks.handedOnThroughWorkflow')
          : t('leaderTasks.handedOnTo', { name: person ? localized(person) : '' }),
      );
      navigate(`/leader-tasks/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  const incomplete =
    !nameFits(f.title) ||
    !hasDescription(f.description) ||
    !f.startAt ||
    !f.dueAt ||
    (f.to === 'person' ? !f.assigneeUserId : !levelsReady(levels));

  return (
    <FormPage
      title={t('leaderTasks.handOnTitle')}
      subtitle={task?.title}
      backTo={`/leader-tasks/${id}`}
      backLabel={task?.title ?? t('nav.leaderTasks')}
      submitLabel={t('leaderTasks.handOn')}
      submitting={handOn.isPending}
      loading={existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      disabled={!!blocked || incomplete || datesBackwards || uploads.busy || (offProject && !overrides)}
      onSubmit={() => handOn.mutate()}
      summary={
        task?.attachments?.length > 0 && (
          <div>
            <p className="mb-1.5 text-caption text-muted">{t('leaderTasks.filesGoWith')}</p>
            <AttachmentList attachments={task.attachments} />
          </div>
        )
      }
    >
      <div className="space-y-4">
        {blocked && <Notice>{blocked}</Notice>}
        <ChoiceCards
          name="hand-on-to"
          value={f.to}
          onChange={(to) => set({ to, assigneeUserId: '' })}
          options={[
            { value: 'person', label: t('leaderTasks.toPerson'), hint: t('leaderTasks.toPersonHint') },
            {
              value: 'workflow',
              label: t('leaderTasks.toWorkflow'),
              hint: t('leaderTasks.toWorkflowHint'),
              // A workflow is built of the same people a person is picked from.
              disabled: nobody,
            },
          ]}
        />
        {f.to === 'person' ? (
          <Field
            label={t('leaderTasks.person')}
            required
            hint={nobody ? undefined : t('leaderTasks.personHint', { name: leaderName })}
            error={
              nobody
                ? t('leaderTasks.nobodyToHandTo', { name: leaderName })
                : offProject && !overrides
                  ? t('leaderTasks.notOnProjectError', { name: localized(person) })
                  : null
            }
          >
            <Select
              value={f.assigneeUserId}
              onChange={(e) => set({ assigneeUserId: e.target.value })}
              disabled={!!blocked}
            >
              <option value="">—</option>
              {(options.data?.departments ?? []).map((department) => (
                <optgroup key={department.id} label={localized(department)}>
                  {department.people.map((p) => (
                    <option key={`${department.id}-${p.id}`} value={p.id}>
                      {localized(p)}
                      {p.onProject === false ? ` (${t('leaderTasks.notOnProject')})` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </Field>
        ) : (
          <div className="rounded-lg border hairline p-4">
            <WorkflowBuilder
              levels={levels}
              onChange={setLevels}
              people={everyone}
              flagged={offProjectOnWorkflow}
              flagLabel={t('leaderTasks.notOnProject')}
              disabled={!!blocked}
              footer={
                <>
                  <p className="mt-3 text-caption text-muted">{t('leaderTasks.workflowPeopleHint', { name: leaderName })}</p>
                  {offProjectOnWorkflow.size > 0 && (
                    <p className={`mt-2 text-caption ${overrides ? 'text-warning-fg dark:text-warning-dark' : 'text-error-fg dark:text-error-dark'}`}>
                      {overrides ? t('tasks.workflowOffProjectOverride') : t('tasks.workflowOffProject')}
                    </p>
                  )}
                </>
              }
            />
          </div>
        )}
        <NameField label={t('common.name')} value={f.title} onChange={(title) => set({ title })} disabled={!!blocked} dir="auto" />
        <Field label={t('common.description')} required>
          <Textarea
            value={f.description}
            onChange={(e) => set({ description: e.target.value })}
            disabled={!!blocked}
            dir="auto"
          />
        </Field>
        <FormGrid>
          <Field label={t('common.project')} hint={t('tasks.generalHint')}>
            <Select
              value={f.projectId}
              onChange={(e) => set({ projectId: e.target.value })}
              disabled={!!blocked}
            >
              <option value="">{t('common.general')}</option>
              {projectChoices.map((p) => (
                <option key={p.id} value={p.id}>{localized(p)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.priority')}>
            <Select value={f.priority} onChange={(e) => set({ priority: e.target.value })} disabled={!!blocked}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('tasks.startDate')} required hint={t('leaderTasks.datesHint')}>
            <Input
              type="datetime-local"
              value={f.startAt}
              onChange={(e) => set({ startAt: e.target.value })}
              disabled={!!blocked}
            />
          </Field>
          <Field label={t('tasks.deadline')} required error={datesBackwards ? t('tasks.dueAfterStart') : null}>
            <Input
              type="datetime-local"
              value={f.dueAt}
              min={f.startAt || undefined}
              onChange={(e) => set({ dueAt: e.target.value })}
              disabled={!!blocked}
            />
          </Field>
        </FormGrid>
        {!blocked && <AttachmentsField uploads={uploads} />}
      </div>
    </FormPage>
  );
}

/**
 * Route: `/leader-tasks/:id/reassign` — the Leaders Task goes to another leader
 * (or to another department of its leader's): by the leader who sent it, the
 * leader who has it, or an admin. Taking it from a leader needs a reason, which
 * they are told. Work already handed on carries on, and the new leader reviews it.
 */
export function LeaderTaskReassignPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const leaderLabel = useLeaderLabel();
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');

  const existing = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const task = existing.data;
  const leaders = useQuery({
    queryKey: ['leader-tasks', 'leaders'],
    queryFn: () => api.get<LeaderOption[]>('/leader-tasks/leaders'),
    enabled: !!task?.can?.reassign,
  });
  // Never back to whoever sent it, and never to where it is already.
  const choices = leaderChoices(leaders.data).filter(
    (c) => c.leader.id !== task?.createdById && c.key !== `${task?.assigneeUserId}|${task?.departmentId}`,
  );
  const chosen = choices.find((c) => c.key === to) ?? null;
  const changesHands = !!chosen && chosen.leader.id !== task?.assigneeUserId;
  const allowed = !!task?.can?.reassign;

  const run = useMutation({
    mutationFn: () =>
      api.post(`/leader-tasks/${id}/reassign`, {
        assigneeUserId: chosen!.leader.id,
        departmentId: chosen!.department.id,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      for (const queryKey of [['task', id], ['leader-tasks'], ['dashboard']]) void queryClient.invalidateQueries({ queryKey });
      toast.success(t('leaderTasks.reassigned', { name: chosen ? localized(chosen.leader) : '' }));
      // Given away by the leader who had it, it is no longer theirs to open.
      const stillOpens = task.createdById === user?.id || can(P.TASKS_MANAGE_ALL) || chosen?.leader.id === user?.id;
      navigate(stillOpens ? `/leader-tasks/${id}` : '/leader-tasks', { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('leaderTasks.reassignTitle')}
      subtitle={task?.title}
      backTo={`/leader-tasks/${id}`}
      backLabel={task?.title ?? t('nav.leaderTasks')}
      submitLabel={t('leaderTasks.reassign')}
      submitting={run.isPending}
      loading={existing.isLoading || (allowed && leaders.isLoading)}
      error={existing.error ?? leaders.error}
      onRetry={() => void (existing.error ? existing.refetch() : leaders.refetch())}
      disabled={!allowed || !chosen || (changesHands && !note.trim())}
      onSubmit={() => run.mutate()}
    >
      <div className="space-y-4">
        {task && !allowed && (
          <Notice>
            {['DONE', 'CANCELLED'].includes(task.status) ? t('tasks.finishedNoReassign') : t('leaderTasks.reassignNotYours')}
          </Notice>
        )}
        <FormGrid>
          <Field label={t('leaderTasks.currentLeader')}>
            <Input
              value={task ? leaderLabel({ leader: task.assignee, department: task.department ?? { name: '—' } }) : ''}
              disabled
              readOnly
            />
          </Field>
          <Field label={t('leaderTasks.newLeader')} required>
            <Select value={to} onChange={(e) => setTo(e.target.value)} disabled={!allowed} autoFocus>
              <option value="">—</option>
              {choices.map((choice) => (
                <option key={choice.key} value={choice.key}>{leaderLabel(choice)}</option>
              ))}
            </Select>
          </Field>
        </FormGrid>
        <Field label={t('common.reason')} required={changesHands} hint={t('leaderTasks.reassignNote')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!allowed} dir="auto" />
        </Field>
      </div>
    </FormPage>
  );
}
